import { useTheme } from '@mui/material';

import type { IAccordionData } from '@rumblefish/ui/src/components/Rumblefish23Theme/Accordion/CustomizedAccordion.types';

import { StyledLink } from './styles';

export const faqData = {
  mainText: `FAQ`,
  description: `NFT development services with Rumble Fish`,
};

export const AccordionData = (): IAccordionData[] => {
  const theme = useTheme();
  return [
    {
      title: 'Q: What is a non-fungible token (NFT)?',
      description: `
        A: An NFT is a type of digital asset that represents ownership or proof 
        of authenticity of a unique item or piece of content on the blockchain. 
        Unlike cryptocurrencies such as Bitcoin or Ethereum, which are fungible 
        and can be exchanged on a one-to-one basis, NFTs are unique and cannot 
        be exchanged on a like-for-like basis.
      `,
      gradientOnFocus: theme.mixins.grapeGradient?.background as string,
    },

    {
      title: 'Q: What are the most common uses of NFTs?',
      customDescription: () => {
        return (
          <>
            A:
            <ul>
              <li>
                <b>Digital Art: </b>
                Artists can create and sell digital artwork as NFTs, providing
                proof of authenticity and ownership to buyers. This has opened
                new revenue streams for artists and creators.
              </li>
              <li>
                <b>Collectibles: </b>
                NFTs are used for digital collectibles, including virtual
                trading cards, in-game items, and other unique digital objects.
                Examples include CryptoKitties and NBA Top Shot.
              </li>
              <li>
                <b>Gaming: </b>
                In the gaming industry, NFTs represent in-game assets such as
                characters, items, or skins. Players can buy, sell, and trade
                these assets, adding real-world value to virtual items.
              </li>
              <li>
                <b>Music and Media: </b>
                Musicians and content creators can tokenize their work, offering
                limited editions or exclusive content as NFTs. This allows them
                to monetize their creations directly and engage with fans in new
                ways. (see{' '}
                <StyledLink to="/case-studies/revelator/">
                  our case study with Revelator
                </StyledLink>{' '}
                to see it implemented in real life).
              </li>
              <li>
                <b>Real Estate: </b>
                NFTs can represent ownership of virtual real estate in digital
                worlds or even tokenize physical real estate, enabling
                fractional fractional ownership and easier transfer of property
                rights.
              </li>
              <li>
                <b>Domain Names: </b>
                NFTs can be used to buy and sell blockchain-based domain names,
                providing a decentralized way to manage domain ownership.
              </li>
            </ul>
          </>
        );
      },
      gradientOnFocus: theme.mixins.violetGradient?.background as string,
    },
    {
      title: 'Q: What is the difference between NFTs and cryptocurrencies?',
      description: `
        A: The primary difference between NFTs (Non-Fungible Tokens) and 
        cryptocurrencies lies in their fungibility and unique properties. 
        <br />
        <br />
        <b>Fungibility</b>
        <ul>
          <li>
            <b>Cryptocurrencies: </b>
            <br />
            Cryptocurrencies like Bitcoin, Ethereum, and others are fungible, 
            meaning each unit is identical and interchangeable with another unit 
            of the same value. For example, 1 Bitcoin is always equal to 1 
            Bitcoin, and it can be exchanged on a one-to-one basis. They operate 
            similarly to traditional currencies where each unit has the same 
            value, making them suitable for use as a medium of exchange, store 
            of value, and unit of account.
          </li>
          <li>
            <b>NFTs: </b>
            <br />
            NFTs are non-fungible, meaning each token is unique and cannot be 
            directly exchanged for another NFT on a one-to-one basis. Each NFT 
            has distinct properties and value, often due to its content, 
            creator, or metadata. NFTs represent ownership of a specific item or 
            piece of content, such as digital art, collectibles, in-game assets, 
            or music. Each NFT is distinct and cannot be replaced with another 
            NFT without a loss of value.
          </li>
        </ul>

        <br />
        <b>Use Cases</b>
        <ul>
          <li>
            <b>Cryptocurrencies: </b>
            <br />
            Used primarily as a digital currency for transactions, investments, 
            and as a store of value. Examples include Bitcoin for peer-to-peer 
            transactions and Ethereum for smart contracts and decentralized 
            applications (dApps). Crypto facilitates online purchases, 
            remittances, and financial transactions without the need for 
            intermediaries.
          </li>
          <li>
            <b>NFTs: </b>
            <br />
            Represent ownership of unique digital or physical assets, including 
            art, music, videos, virtual real estate, and collectibles. They also 
            provide proof of authenticity and ownership, often with a verifiable 
            history of previous owners and transactions recorded on the 
            blockchain.
          </li>
        </ul>
      `,
      gradientOnFocus: theme.mixins.blueGradient?.background as string,
    },
    {
      title: 'Q: How do NFTs work?',
      description: `
        A: NFTs (Non-Fungible Tokens) are unique digital assets verified using 
        blockchain technology. Here’s how they work: 
        <br />
        <ul>
          <li>
            <b>Blockchain: </b>
            NFTs are recorded on a blockchain, typically Ethereum, ensuring 
            security and transparency.
          </li>
          <li>
            <b>Smart Contracts: </b>
            NFTs are governed by smart contracts that define their uniqueness 
            and manage ownership.
          </li>
          <li>
            <b>Minting: </b>
            Creating an NFT involves uploading a digital file (art, video, 
            music) to the blockchain, embedding it with unique metadata.
          </li>
          <li>
            <b>Ownership: </b>
            Ownership and transaction history are verifiable on the blockchain, 
            providing proof of authenticity.
          </li>
          <li>
            <b>Marketplaces: </b>
            NFTs can be bought and sold on platforms like OpenSea and Rarible 
            using cryptocurrencies.
          </li>
          <li>
            <b>Royalties: </b>
            Creators can earn royalties on secondary sales through smart 
            contracts.
          </li>
          <li>
            <b>Interoperability: </b>
            NFTs can be traded across different platforms supporting the same 
            blockchain standards.
          </li>
        </ul>

        Example:
        <br />
        An artist mints a digital painting as an NFT on Ethereum. A buyer 
        purchases it on OpenSea, with the transaction recorded on the 
        blockchain. If resold, the artist receives a royalty automatically. 
        NFTs enable unique ownership of digital assets, offering new 
        monetization avenues for creators while ensuring authenticity 
        and provenance.
      `,
      gradientOnFocus: theme.mixins.tealGradient?.background as string,
    },
    {
      title: 'Q: Why hire Rumble Fish for NFT platform development?',
      description: `
        A: At Rumble Fish, we combine cutting-edge technology with deep industry 
        expertise to deliver exceptional NFT platform development services. 
        Here’s why you should choose us: 
        <br />
        <ul>
          <li>
            <b>Expertise: </b>
            Our team consists of seasoned blockchain developers and smart 
            contract experts who have extensive experience in building robust 
            and scalable NFT platforms.
          </li>
          <li>
            <b>Customization: </b>
            We create tailored solutions that meet your specific business needs, 
            whether you’re looking to develop an NFT marketplace, minting 
            platform, or a specialized NFT application.
          </li>
          <li>
            <b>Security: </b>
            We prioritize the security of your platform by implementing the 
            latest standards and best practices, ensuring your NFTs and 
            transactions are secure and tamper-proof.
          </li>
          <li>
            <b>User Experience: </b>
            Our focus on seamless and intuitive UX/UI design ensures that your 
            platform is not only functional but also user-friendly and engaging.
          </li>
          <li>
            <b>Support and Maintenance: </b>
            We offer ongoing support and maintenance services to ensure your 
            NFT platform remains up-to-date and performs optimally.
          </li>
          <li>
            <b>Innovation: </b>
            We stay ahead of industry trends and incorporate the latest 
            advancements in blockchain technology to provide you with 
            innovative and future-proof solutions.
          </li>
        </ul>
        Partner with Rumble Fish to leverage our expertise and bring your NFT 
        platform vision to life with confidence and excellence.
      `,
      gradientOnFocus: theme.mixins.greenGradient?.background as string,
    },
  ];
};
